const GA4_MEASUREMENT_ID = "G-3G7V1CVWQ6";
const UTM_STORAGE_KEY = "m2go_utm_attribution";

const canTrack = () =>
	typeof window !== "undefined" && typeof window.gtag === "function";

const canTrackFb = () =>
	typeof window !== "undefined" && typeof window.fbq === "function";

const trackFbEvent = (name, parameters, eventId) => {
	if (!canTrackFb()) return false;
	window.fbq(
		"track",
		name,
		parameters || {},
		eventId ? { eventID: String(eventId) } : {}
	);
	return true;
};

const trackFbCustomEvent = (name, parameters) => {
	if (!canTrackFb()) return false;
	window.fbq("trackCustom", name, parameters || {});
	return true;
};

const numberValue = (value) => {
	const parsed = parseFloat(value);
	return isNaN(parsed) ? 0 : parsed;
};

export const ga4Item = (item, quantity) => ({
	item_id: String(item.id || item.item_id || ""),
	item_name: item.name || item.item_name || "Item",
	item_category: item.restaurant_id ? String(item.restaurant_id) : undefined,
	price: numberValue(item.price),
	quantity: numberValue(quantity || item.quantity || 1),
});

export const trackEvent = (name, parameters) => {
	if (!canTrack()) return false;
	window.gtag("event", name, parameters || {});
	return true;
};

export const trackPageView = (page) => {
	if (!canTrack()) return;
	// React Router can strip the ?utm_... params from the URL before this
	// fires (a client-side redirect does not preserve the query string).
	// getStoredAttribution() was captured earlier, at app boot, so it still
	// has the real campaign data - send it as GA4 manual campaign params so
	// the session is attributed correctly even when the URL no longer has it.
	const attribution = getStoredAttribution();
	window.gtag("event", "page_view", {
		send_to: GA4_MEASUREMENT_ID,
		page_path: page,
		page_location: window.location.href,
		page_title: document.title,
		...(attribution.utm_source && {
			source: attribution.utm_source,
			medium: attribution.utm_medium,
			campaign: attribution.utm_campaign,
			content: attribution.utm_content,
		}),
	});
};

export const trackViewItem = (item) => {
	if (!item || !item.id) return;
	trackEvent("view_item", {
		currency: "USD",
		value: numberValue(item.price),
		items: [ga4Item(item, 1)],
	});
	trackFbEvent("ViewContent", {
		content_ids: [String(item.id)],
		content_type: "product",
		currency: "USD",
		value: numberValue(item.price),
	});
};

export const trackAddToCart = (item, addedQuantity) => {
	if (!item || !item.id) return;
	const quantity = numberValue(addedQuantity || 1);
	trackEvent("add_to_cart", {
		currency: "USD",
		value: numberValue(item.price) * quantity,
		items: [ga4Item(item, quantity)],
	});
	trackFbEvent("AddToCart", {
		content_ids: [String(item.id)],
		content_type: "product",
		currency: "USD",
		value: numberValue(item.price) * quantity,
	});
};

export const trackCart = (name, products, value) => {
	if (!products || !products.length) return;
	const itemCount = products.reduce(
		(total, item) => total + numberValue(item.quantity || 1),
		0
	);
	const restaurantId = products[0].restaurant_id
		? String(products[0].restaurant_id)
		: undefined;
	trackEvent(name, {
		currency: "USD",
		value: numberValue(value),
		num_items: itemCount,
		restaurant_id: restaurantId,
		items: products.map((item) => ga4Item(item)),
	});
	if (name === "view_cart") {
		trackFbCustomEvent("ViewCart", {
			content_ids: products.map((item) => String(item.id || item.item_id || "")),
			content_type: "product",
			currency: "USD",
			value: numberValue(value),
			num_items: itemCount,
			restaurant_id: restaurantId,
		});
	}
	if (name === "begin_checkout") {
		trackFbEvent("InitiateCheckout", {
			content_ids: products.map((item) => String(item.id || item.item_id || "")),
			content_type: "product",
			currency: "USD",
			value: numberValue(value),
			num_items: itemCount,
			restaurant_id: restaurantId,
		});
	}
};

export const trackPaymentInfo = (method, products, value) => {
	if (!products || !products.length) return;
	trackEvent("add_payment_info", {
		currency: "USD",
		value: numberValue(value),
		payment_type: method || "unknown",
		items: products.map((item) => ga4Item(item)),
	});
	trackFbEvent("AddPaymentInfo", {
		content_ids: products.map((item) => String(item.id || item.item_id || "")),
		content_type: "product",
		currency: "USD",
		value: numberValue(value),
	});
};

export const trackDeliveryInfo = (products, value, details = {}) => {
	if (!products || !products.length) return;

	const restaurantId = details.restaurant_id
		? String(details.restaurant_id)
		: products[0].restaurant_id
		? String(products[0].restaurant_id)
		: undefined;
	const deliveryType = details.delivery_type || "delivery";
	const parameters = {
		currency: "USD",
		value: numberValue(value),
		shipping_tier: deliveryType,
		restaurant_id: restaurantId,
		delivery_charge: numberValue(details.delivery_charge),
		distance_km: numberValue(details.distance_km),
		free_delivery_threshold: numberValue(details.free_delivery_threshold),
		amount_missing_for_free_delivery: numberValue(
			details.amount_missing_for_free_delivery
		),
		items: products.map((item) => ga4Item(item)),
	};

	const deliveryEventSent = trackEvent("delivery_fee_viewed", parameters);
	const shippingEventSent = trackEvent("add_shipping_info", parameters);
	const metaEventSent = trackFbCustomEvent("DeliveryFeeViewed", {
		content_ids: products.map((item) => String(item.id || item.item_id || "")),
		content_type: "product",
		currency: "USD",
		value: numberValue(value),
		restaurant_id: restaurantId,
		delivery_type: deliveryType,
		delivery_charge: numberValue(details.delivery_charge),
		distance_km: numberValue(details.distance_km),
		free_delivery_threshold: numberValue(details.free_delivery_threshold),
		amount_missing_for_free_delivery: numberValue(
			details.amount_missing_for_free_delivery
		),
	});

	return deliveryEventSent || shippingEventSent || metaEventSent;
};

export const trackPurchase = (checkout, products, coupon, fallbackTotal) => {
	if (!checkout || !checkout.success || !checkout.data) return;

	const order = checkout.data;
	// Skip orders that are still genuinely awaiting payment (8), and never
	// fire for a Canceled (6) or Payment Failed (9) order - a $0/void
	// event with a real order id pollutes GA4 with a fake sale.
	const BAD_STATUSES = [6, 8, 9];
	if (BAD_STATUSES.includes(Number(order.orderstatus_id))) return;

	const transactionId = order.unique_order_id || order.id;
	if (!transactionId) return;

	const purchaseTotal = Number(order.total || order.payable || fallbackTotal || 0);
	if (!purchaseTotal || purchaseTotal <= 0) return;

	const storageKey = "ga4_purchase_sent_" + String(transactionId);
	const fallbackValue =
		fallbackTotal && fallbackTotal.totalPrice !== undefined
			? fallbackTotal.totalPrice
			: fallbackTotal;
	const couponName =
		order.coupon_name ||
		(coupon && coupon.data && (coupon.data.name || coupon.data.code)) ||
		undefined;

	const purchaseValue = numberValue(order.total || order.payable || fallbackValue);

	if (localStorage.getItem(storageKey) !== "true") {
		const sent = trackEvent("purchase", {
			transaction_id: String(transactionId),
			currency: "USD",
			value: purchaseValue,
			tax: numberValue(order.tax_amount || order.tax),
			shipping: numberValue(order.delivery_charge || order.actual_delivery_charge),
			coupon: couponName,
			items: (products || []).map((item) => ga4Item(item)),
		});

		if (sent) {
			localStorage.setItem(storageKey, "true");
		}
	}

	const fbStorageKey = "fb_purchase_sent_" + String(transactionId);
	if (localStorage.getItem(fbStorageKey) !== "true") {
		const fbSent = trackFbEvent(
			"Purchase",
			{
				content_ids: (products || []).map((item) =>
					String(item.id || item.item_id || "")
				),
				content_type: "product",
				currency: "USD",
				value: purchaseValue,
			},
			transactionId
		);
		if (fbSent) {
			localStorage.setItem(fbStorageKey, "true");
		}
	}
};

// ---- Attribution: GA4 client_id / session_id + UTM persistence ----
// The server-side purchase event (sent by the backend once the payment is
// confirmed) needs these to tie back to the real marketing session, instead
// of landing in GA4 as a fresh, campaign-less hit ("Direct").

const readCookie = (name) => {
	if (typeof document === "undefined") return null;
	const match = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
	return match ? decodeURIComponent(match[1]) : null;
};

// The GA4 client_id lives in the first-party _ga cookie, format:
// GA1.1.<part1>.<part2>  ->  client_id is "<part1>.<part2>"
export const getGaClientId = () => {
	const raw = readCookie("_ga");
	if (!raw) return null;
	const parts = raw.split(".");
	if (parts.length < 4) return null;
	return `${parts[2]}.${parts[3]}`;
};

// The GA4 session cookie is named _ga_<container-id> (container id = the
// measurement id without the "G-" prefix). Format: GS1.1.<session_id>...
export const getGaSessionId = () => {
    const containerId = GA4_MEASUREMENT_ID.replace(/^G-/, "");
    const raw = readCookie(`_ga_${containerId}`);
    if (!raw) return null;

    let match = raw.match(/^GS1\.\d+\.(\d+)(?:\.|$)/);

    if (!match && /^GS2\.\d+\./.test(raw)) {
        const fields = raw.split(".").slice(2).join(".");
        match = fields.match(/(?:^|\$)s(\d+)(?:\$|$)/);
    }

    return match && /[1-9]/.test(match[1]) ? match[1] : null;
};

// The user-agent of the customer-facing WebView app has a "Manjar2Go/1.0"
// suffix (confirmed in production access logs) - use it to tell orders
// placed inside the app apart from the regular mobile/desktop website.
export const getSourcePlatform = () => {
	if (typeof navigator === "undefined") return "web";
	return /Manjar2Go/i.test(navigator.userAgent || "") ? "app" : "web";
};

// Call once when the app boots. If the current URL carries UTM params,
// saves them as the visitor's attribution for any order placed later in
// this browser (overwrites on every new campaign visit - "most recent
// marketing touch" model, matching GA4's own default channel grouping).
export const captureAttribution = () => {
	try {
		const params = new URLSearchParams(window.location.search);
		const utmSource = params.get("utm_source");
		if (!utmSource) return;

		localStorage.setItem(
			UTM_STORAGE_KEY,
			JSON.stringify({
				utm_source: utmSource,
				utm_medium: params.get("utm_medium") || "",
				utm_campaign: params.get("utm_campaign") || "",
				utm_content: params.get("utm_content") || "",
				captured_at: Date.now(),
			})
		);
	} catch (e) {
		// localStorage unavailable (private mode, etc.) - attribution is
		// best-effort, never block the app over it.
	}
};

export const getStoredAttribution = () => {
	try {
		const raw = localStorage.getItem(UTM_STORAGE_KEY);
		return raw ? JSON.parse(raw) : {};
	} catch (e) {
		return {};
	}
};

// Everything the backend needs to attribute + tag an order, bundled for
// the placeOrder request payload.
export const getAttributionPayload = () => {
	const stored = getStoredAttribution();
	return {
		ga_client_id: getGaClientId(),
		ga_session_id: getGaSessionId(),
		source_platform: getSourcePlatform(),
		utm_source: stored.utm_source || "",
		utm_medium: stored.utm_medium || "",
		utm_campaign: stored.utm_campaign || "",
		utm_content: stored.utm_content || "",
	};
};
